import React, { useState, useEffect } from 'react';
import { Dice6, X } from 'lucide-react';
import { ConfirmModal } from './ConfirmModal';
import { Character } from '../types';

interface CharacterCardProps {
  character: Character;
  setCharacter: (character: Character) => void;
  importedCharacters?: Character[];
  onImport: (key: string, id: string) => void;
  setActiveCharacterId: (id: string) => void;
}

export const CharacterCard: React.FC<CharacterCardProps> = ({
  character,
  setCharacter,
  importedCharacters = [],
  onImport,
  setActiveCharacterId,
}) => {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [localCharacter, setLocalCharacter] = useState<Character>(character);
  const [selectedImportedCharacter, setSelectedImportedCharacter] = useState<string | null>(
    importedCharacters?.[0]?.id ?? null,
  );
  const [attackRoll, setAttackRoll] = useState<number | null>(null);
  const [defenseRoll, setDefenseRoll] = useState<number | null>(null);
  const [newSpell, setNewSpell] = useState<{ name: string; slots: number }>({ name: '', slots: 0 });
  const [newEquipment, setNewEquipment] = useState<string>('');
  const [isAttackRolling, setIsAttackRolling] = useState<boolean>(false);
  const [isDefenseRolling, setIsDefenseRolling] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const rollDuration = 300;

  useEffect(() => {
    setCharacter(localCharacter);
  }, [localCharacter]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { name, value } = e.target;
    setLocalCharacter((prev) => ({ ...prev, [name]: value }));
  };

  const handleSpellChange = (index: number, e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;

    setLocalCharacter((prev) => {
      const spells = [...prev.spells];
      let newSlotsValue: boolean[] = [];

      if (name === 'slots') {
        const newValue = value ? parseInt(value, 10) : 0;
        const currentSlots = spells[index].checkedSlots;

        if (currentSlots.length > newValue) {
          // Reduce the number of checked slots if the new value is less
          newSlotsValue = currentSlots.slice(0, newValue);
        } else if (currentSlots.length < newValue) {
          // Increase the number of checked slots if the new value is more
          newSlotsValue = [...currentSlots, ...Array(newValue - currentSlots.length).fill(true)];
        } else {
          // If the number of slots remains the same, keep the current slots
          newSlotsValue = [...currentSlots];
        }
      }

      // Update the spell at the specific index
      spells[index] = {
        ...spells[index],
        [name]: name === 'slots' ? parseInt(value, 10) : value,
        checkedSlots: name === 'slots' ? newSlotsValue : spells[index].checkedSlots,
      };

      return { ...prev, spells };
    });
  };

  const addSpell = (): void => {
    if (newSpell.name.trim()) {
      const newSpellSlots = Number.isNaN(+newSpell.slots) ? 0 : newSpell.slots;

      setLocalCharacter((prev) => ({
        ...prev,
        spells: [
          ...prev.spells,
          {
            ...newSpell,
            slots: parseInt(String(newSpellSlots), 10),
            checkedSlots: Array(parseInt(String(newSpellSlots), 10)).fill(true),
          },
        ],
      }));
      setNewSpell({ name: '', slots: 0 });
    }
  };

  const deleteSpell = (indexToDelete: number): void => {
    setLocalCharacter((prev) => ({
      ...prev,
      spells: prev.spells.filter((_, i) => i !== indexToDelete),
    }));
  };

  const toggleSlotChecked = (spellIndex: number, slotIndex: number): void => {
    setLocalCharacter((prev) => {
      const spells = prev.spells.map((spell, index) => {
        if (index !== spellIndex) return spell;
        const checkedSlots = spell.checkedSlots.map((slot, i) => (i === slotIndex ? !slot : slot));
        return { ...spell, checkedSlots };
      });
      return { ...prev, spells };
    });
  };

  const addEquipment = (): void => {
    if (newEquipment.trim()) {
      setLocalCharacter((prev) => ({
        ...prev,
        equipment: [...prev.equipment, newEquipment.trim()],
      }));
      setNewEquipment('');
    }
  };

  const deleteEquipment = (itemIndex: number): void => {
    setLocalCharacter((prev) => ({
      ...prev,
      equipment: prev.equipment.filter((_, i) => i !== itemIndex),
    }));
  };

  const toggleEditMode = (): void => {
    setIsEditMode(!isEditMode);
  };

  const rollAttack = (): void => {
    setIsAttackRolling(true);
    setAttackRoll(null);

    setTimeout(() => {
      setAttackRoll(Math.floor(Math.random() * 6) + 1);
      setIsAttackRolling(false);
    }, rollDuration);
  };

  const rollDefense = (): void => {
    setIsDefenseRolling(true);
    setDefenseRoll(null);

    setTimeout(() => {
      setDefenseRoll(Math.floor(Math.random() * 6) + 1);
      setIsDefenseRolling(false);
    }, rollDuration);
  };

  const incrementLife = (): void => {
    setLocalCharacter((prev) => ({
      ...prev,
      currentLife: Math.min(prev.currentLife + 1, prev.fullLife),
    }));
  };

  const decrementLife = (): void => {
    setLocalCharacter((prev) => ({
      ...prev,
      currentLife: Math.max(prev.currentLife - 1, 0),
    }));
  };

  const handleImportCharacter = (): void => {
    setIsModalOpen(true);
  };

  const confirmImportCharacter = (): void => {
    setIsModalOpen(false);
    const foundCharacter = importedCharacters.find((c) => c.id === selectedImportedCharacter);
    if (foundCharacter) {
      const updatedCharacter: Character = {
        ...foundCharacter,
        key: localCharacter.key,
      };
      setCharacter(updatedCharacter);
      setActiveCharacterId(updatedCharacter.id);
      onImport(foundCharacter.key, foundCharacter.id); // Clear the character from the original dungeon
      toggleEditMode();
    }
  };

  const cancelImportCharacter = (): void => {
    setIsModalOpen(false);
  };

  return (
    <div className="dark:bg-gray-800 dark:text-white p-4 bg-gray-100 rounded-b rounded-tr">
      {isEditMode ? (
        <div className="flex flex-col flex-wrap">
          {importedCharacters.length > 0 && (
            <div className="mb-6">
              <select
                value={selectedImportedCharacter ?? ''}
                onChange={(e) => setSelectedImportedCharacter(e.target.value)}
                className="dark:bg-gray-800 bg-white border border-gray-300 rounded-md py-1 px-1 text-ellipsis"
              >
                {importedCharacters.map((char, i) => (
                  <option className="text-ellipsis" key={i + 1} value={char.id}>
                    {char.name} {char.class} Level: {char.level}
                  </option>
                ))}
              </select>
              <button
                className="ml-2 bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 transition-colors"
                onClick={handleImportCharacter}
              >
                Migrate
              </button>
              {isModalOpen && (
                <ConfirmModal
                  title="Confirm Migration"
                  text="The character will be removed from the original dungeon and the current slot will be entirely rewritten. Are you sure you want to migrate the character?"
                  isOpen={isModalOpen}
                  onClose={cancelImportCharacter}
                  onConfirm={confirmImportCharacter}
                />
              )}
            </div>
          )}
          <div className="flex space-x-4">
            <div>
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm dark:text-slate-400 font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={localCharacter.name}
                  onChange={handleInputChange}
                  placeholder="Name"
                  className="w-[100px] dark:bg-gray-800 mt-1 block p-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label
                  htmlFor="class"
                  className="block text-sm dark:text-slate-400 font-medium text-gray-700"
                >
                  Class
                </label>
                <input
                  type="text"
                  id="class"
                  name="class"
                  value={localCharacter.class}
                  onChange={handleInputChange}
                  placeholder="Class"
                  className="w-[100px] dark:bg-gray-800 mt-1 block p-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label
                  htmlFor="level"
                  className="block text-sm dark:text-slate-400 font-medium text-gray-700"
                >
                  Level
                </label>
                <input
                  type="number"
                  id="level"
                  name="level"
                  value={localCharacter.level}
                  onChange={handleInputChange}
                  placeholder="Level"
                  className="w-[100px] dark:bg-gray-800 mt-1 block p-2 border border-gray-300 rounded"
                  min="1"
                />
              </div>
            </div>
            <div>
              <div>
                <label
                  htmlFor="gold"
                  className="block text-sm dark:text-slate-400 font-medium text-gray-700"
                >
                  Gold
                </label>
                <input
                  type="number"
                  id="gold"
                  name="gold"
                  value={localCharacter.gold}
                  onChange={handleInputChange}
                  placeholder="Gold"
                  className="w-[100px] dark:bg-gray-800 mt-1 block p-2 border border-gray-300 rounded"
                  min="0"
                />
              </div>
              <div>
                <label
                  htmlFor="attack"
                  className="block text-sm dark:text-slate-400 font-medium text-gray-700"
                >
                  Attack
                </label>
                <input
                  type="number"
                  id="attack"
                  name="attack"
                  value={localCharacter.attack}
                  onChange={handleInputChange}
                  placeholder="Attack"
                  className="w-[100px] dark:bg-gray-800 mt-1 block p-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label
                  htmlFor="defense"
                  className="block text-sm dark:text-slate-400 font-medium text-gray-700"
                >
                  Defense
                </label>
                <input
                  type="number"
                  id="defense"
                  name="defense"
                  value={localCharacter.defense}
                  onChange={handleInputChange}
                  placeholder="Defense"
                  className="w-[100px] dark:bg-gray-800 mt-1 block p-2 border border-gray-300 rounded"
                />
              </div>
            </div>
          </div>
          <div className="flex space-x-4">
            <div>
              <label
                htmlFor="fullLife"
                className="block text-sm dark:text-slate-400 font-medium text-gray-700"
              >
                Full Life
              </label>
              <input
                type="number"
                id="fullLife"
                name="fullLife"
                value={localCharacter.fullLife}
                onChange={handleInputChange}
                placeholder="Full Life"
                className="w-[100px] dark:bg-gray-800 mt-1 block p-2 border border-gray-300 rounded"
                min="0"
              />
            </div>
            <div>
              <label
                htmlFor="currentLife"
                className="block text-sm dark:text-slate-400 font-medium text-gray-700"
              >
                Current Life
              </label>
              <input
                type="number"
                id="currentLife"
                name="currentLife"
                value={localCharacter.currentLife}
                onChange={handleInputChange}
                placeholder="Current Life"
                className="dark:bg-gray-800 w-[100px] mt-1 block p-2 border border-gray-300 rounded"
                min="0"
                max={localCharacter.fullLife}
              />
            </div>
          </div>
          <div className="mt-4">
            <h3 className="text-lg font-semibold">Equipment</h3>
            <ul>
              {localCharacter.equipment.map((item, index) => (
                <li key={index} className="flex my-2">
                  <div>{item}</div>
                  <button
                    className="ml-2 px-1 inline border bg-red-500 rounded"
                    onClick={() => deleteEquipment(index)}
                  >
                    <X className="text-white" size={16} />
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex space-x-2 mt-2">
              <input
                type="text"
                value={newEquipment}
                onChange={(e) => setNewEquipment(e.target.value)}
                placeholder="Equipment Name"
                className="max-w-[216px] dark:bg-gray-800 p-2 border border-gray-300 rounded"
              />
              <button
                onClick={addEquipment}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
              >
                + Add Equipment
              </button>
            </div>
          </div>
          <div className="mt-4">
            <h3 className="text-lg font-semibold">Spells</h3>
            {localCharacter.spells.map((spell, index) => (
              <div key={index} className="flex space-x-2 mt-2 items-center">
                <input
                  type="text"
                  name="name"
                  value={spell.name}
                  onChange={(e) => handleSpellChange(index, e)}
                  placeholder="Spell Name"
                  className="max-w-[148px] dark:bg-gray-800 p-2 border border-gray-300 rounded"
                />
                <input
                  type="number"
                  name="slots"
                  value={spell.slots}
                  onChange={(e) => handleSpellChange(index, e)}
                  placeholder="Slots"
                  className="dark:bg-gray-800 w-[60px] p-2 border border-gray-300 rounded"
                  min="0"
                />
                <button
                  onClick={() => deleteSpell(index)}
                  className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                  title="Delete Spell"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
            <div className="flex space-x-2 mt-2">
              <input
                type="text"
                value={newSpell.name}
                onChange={(e) =>
                  setNewSpell({
                    ...newSpell,
                    name: e.target.value,
                  })
                }
                placeholder="Spell Name"
                className="max-w-[148px] dark:bg-gray-800 p-2 border border-gray-300 rounded"
              />
              <input
                type="number"
                value={newSpell.slots}
                onChange={(e) =>
                  setNewSpell({
                    ...newSpell,
                    slots: parseInt(e.target.value, 10),
                  })
                }
                placeholder="Slots"
                className="dark:bg-gray-800 w-[60px] p-2 border border-gray-300 rounded"
                min="0"
              />
              <button
                onClick={addSpell}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
              >
                + Add Spell
              </button>
            </div>
          </div>
          <div className="mt-4">
            <h3 className="text-lg font-semibold">Notes</h3>
            <textarea
              id="notes"
              name="notes"
              value={localCharacter.notes}
              onChange={handleInputChange}
              placeholder="Notes"
              className="dark:bg-gray-800 mt-1 block w-1/2 p-2 border border-gray-300 rounded"
            />
          </div>
          <div>
            <button
              onClick={() => {
                setCharacter(localCharacter);
                toggleEditMode();
              }}
              className="bg-blue-500 text-white px-4 py-2 rounded mt-4 hover:bg-blue-600 transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-6">
              <p className="text-sm dark:text-slate-400 text-gray-500 mb-0">
                <strong>Level:</strong> {localCharacter.level}
              </p>
              <div className="flex items-center space-x-2">
                <p className="text-sm dark:text-slate-400 text-gray-500 mb-0">
                  <strong>Life:</strong> {localCharacter.currentLife}/{localCharacter.fullLife}
                </p>
                <button
                  onClick={incrementLife}
                  className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600 transition-colors"
                >
                  +1
                </button>
                <button
                  onClick={decrementLife}
                  className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 transition-colors"
                >
                  -1
                </button>
              </div>
              <p className="text-sm dark:text-slate-400 text-gray-500 mb-0">
                <strong>Gold:</strong> {localCharacter.gold}
              </p>
            </div>
            <button
              onClick={toggleEditMode}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
            >
              Edit
            </button>
          </div>

          <div className="flex flex-col gap-3 mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm dark:text-slate-400 text-gray-500">
                <strong>Attack:</strong> {character.attack}
              </span>
              <button
                onClick={rollAttack}
                disabled={isAttackRolling}
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-2 h-[40px] rounded
                           hover:from-green-600 hover:to-emerald-700 transition-all duration-300
                           shadow-lg hover:shadow-xl
                           disabled:opacity-50 disabled:cursor-not-allowed
                           font-medium flex items-center gap-2"
              >
                <div className={`flex ${isAttackRolling ? 'animate-bounce' : ''}`}>
                  <Dice6
                    className={`inline-block ${isAttackRolling ? 'animate-spin' : ''}`}
                    size={20}
                  />
                </div>
              </button>
              {attackRoll !== null && (
                <div className="animate-bounce-in">
                  <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white px-2 py-1 rounded shadow-lg flex items-center justify-center min-w-[40px] h-[40px]">
                    <p className="text-2xl font-bold">{attackRoll}</p>
                  </div>
                </div>
              )}
              {attackRoll !== null && (
                <span className="text-sm dark:text-white text-gray-700">
                  ({localCharacter.attack >= 0 ? '+' : ''}
                  {localCharacter.attack})
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm dark:text-slate-400 text-gray-500">
                <strong>Defense:</strong> {character.defense}
              </span>
              <button
                onClick={rollDefense}
                disabled={isDefenseRolling}
                className="bg-gradient-to-r from-yellow-500 to-amber-600 text-white px-3 py-2 h-[40px] rounded
                           hover:from-yellow-600 hover:to-amber-700 transition-all duration-300
                           shadow-lg hover:shadow-xl
                           disabled:opacity-50 disabled:cursor-not-allowed
                           font-medium flex items-center gap-2"
              >
                <div className={`flex ${isDefenseRolling ? 'animate-bounce' : ''}`}>
                  <Dice6
                    className={`inline-block ${isDefenseRolling ? 'animate-spin' : ''}`}
                    size={20}
                  />
                </div>
              </button>
              {defenseRoll !== null && (
                <div className="animate-bounce-in">
                  <div className="bg-gradient-to-br from-amber-400 to-orange-500 text-white px-2 py-1 rounded shadow-lg flex items-center justify-center min-w-[40px] h-[40px]">
                    <p className="text-2xl font-bold">{defenseRoll}</p>
                  </div>
                </div>
              )}
              {defenseRoll !== null && (
                <span className="text-sm dark:text-white text-gray-700">
                  ({localCharacter.defense >= 0 ? '+' : ''}
                  {localCharacter.defense})
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 space-y-6">
            <div className="md:w-1/2">
              <h3 className="text-sm dark:text-slate-400 text-gray-500 font-semibold mb-1">
                Equipment
              </h3>
              <ul className="text-sm dark:text-slate-300 text-gray-700 list-none">
                {localCharacter.equipment.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="md:w-1/2 mt-4 md:mt-0">
              <h3 className="text-sm dark:text-slate-400 text-gray-500 font-semibold mb-1">
                Spells
              </h3>
              <ul className="text-sm dark:text-slate-300 text-gray-700 list-none">
                {localCharacter.spells.map((spell, index) => (
                  <li key={index} className="flex items-center">
                    {spell.name}
                    <div className="ml-1 flex space-x-1">
                      {spell.checkedSlots.map((checked, i) => (
                        <button
                          key={i}
                          className={`w-4 h-4 border rounded ${
                            checked ? 'bg-green-500' : 'bg-red-500'
                          }`}
                          onClick={() => toggleSlotChecked(index, i)}
                        />
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-4 text-sm dark:text-slate-400 text-gray-500">
            <strong>Notes:</strong>
            <p className="whitespace-pre-wrap text-sm dark:text-slate-300 text-gray-700 mt-1">
              {localCharacter.notes}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
